/**
 * RecordingEditModal — teacher-only editor for a class recording.
 * Mirrors MaterialEditModal so Alberto has a consistent UX to fix the
 * title/description, reassign CEFR levels, swap the video URL, or set a
 * future publish date without deleting and re-uploading.
 *
 * Backend: PATCH /api/recordings/{recording_id}
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Pencil, X, FloppyDisk, Spinner } from '@phosphor-icons/react';
import { isDraftDate, scheduleForSave, DRAFT_HINT } from '../lib/scheduleDraft';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1'];

export default function RecordingEditModal({ recording, open, onClose, onSaved }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [levels, setLevels] = useState([]);
  const [recordedAt, setRecordedAt] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!recording) return;
    setTitle(recording.title || '');
    setDescription(recording.description || '');
    setVideoUrl(recording.video_url || '');
    setLevels(recording.levels?.length ? [...recording.levels] : (recording.level ? [recording.level] : ['A2']));
    setRecordedAt(recording.recorded_at || '');
    // scheduled_at is stored as UTC ISO; convert back to local for the
    // <input type="datetime-local"> picker so the teacher sees their own
    // timezone.
    const raw = recording.scheduled_at || '';
    if (!raw || isDraftDate(raw)) {
      setScheduledAt('');
    } else {
      try {
        const d = new Date(raw);
        const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
        setScheduledAt(local.toISOString().slice(0, 16));
      } catch {
        setScheduledAt(raw.slice(0, 16));
      }
    }
  }, [recording]);

  if (!open || !recording) return null;

  const toggleLvl = (l) => setLevels(ls => ls.includes(l) ? ls.filter(x => x !== l) : [...ls, l]);

  const save = async () => {
    if (!title.trim()) return toast.error('Titel fehlt');
    if (levels.length === 0) return toast.error('Mind. 1 Niveau');
    setBusy(true);
    try {
      // datetime-local is local time — convert to a UTC ISO so the backend
      // and read filter compare on the same clock (see MaterialsPage note).
      const sched = scheduleForSave(scheduledAt, recording.scheduled_at);
      const body = {
        title: title.trim(),
        description: description.trim(),
        video_url: videoUrl.trim(),
        levels,
        recorded_at: recordedAt || null,
        scheduled_at: sched,
      };
      const res = await axios.patch(`${API}/recordings/${recording.recording_id}`, body, { withCredentials: true });
      toast.success('Aufzeichnung aktualisiert');
      onSaved?.(res.data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="recording-edit-modal">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-[#292524] flex items-center gap-2">
            <Pencil size={18} weight="duotone" className="text-[#AB1C24]" />
            Aufzeichnung bearbeiten
          </h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600" data-testid="recording-edit-close">
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="space-y-2">
          <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Titel *</label>
          <input value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-stone-50 border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid="recording-edit-title" />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Beschreibung</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className="w-full bg-stone-50 border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid="recording-edit-description" />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Video-URL</label>
          <input value={videoUrl} onChange={e => setVideoUrl(e.target.value)} className="w-full bg-stone-50 border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid="recording-edit-video-url" />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Niveaus *</label>
          <div className="flex flex-wrap gap-1.5">
            {ALL_LEVELS.map(l => (
              <button key={l} type="button" onClick={() => toggleLvl(l)}
                className={`px-3 py-1 rounded-full border text-xs font-semibold ${levels.includes(l) ? 'bg-[#AB1C24] border-[#AB1C24] text-white' : 'bg-white border-stone-300 text-[#57534E] hover:border-[#AB1C24]'}`}
                data-testid={`recording-edit-level-${l}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Aufnahmedatum</label>
            <input type="date" value={recordedAt} onChange={e => setRecordedAt(e.target.value)} className="w-full bg-stone-50 border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid="recording-edit-recorded-at" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Veröffentlichung</label>
            <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} className="w-full bg-stone-50 border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid="recording-edit-scheduled-at" />
          </div>
        </div>
        <p className="text-[11px] text-[#78716C] leading-snug">
          {isDraftDate(recording.scheduled_at) ? DRAFT_HINT : 'Veröffentlichung leer lassen = sofort sichtbar. Ein zukünftiges Datum blendet die Aufnahme für Schüler aus, bis der Zeitpunkt erreicht ist.'}
        </p>
        <div className="flex gap-2 pt-2 border-t border-stone-200">
          <Button onClick={onClose} variant="outline" className="flex-1" data-testid="recording-edit-cancel">Abbrechen</Button>
          <Button onClick={save} disabled={busy} className="flex-1 bg-[#AB1C24] hover:bg-[#8A151C] text-white" data-testid="recording-edit-save">
            {busy ? <Spinner size={13} className="animate-spin mr-2" /> : <FloppyDisk size={13} weight="fill" className="mr-2" />}
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}
